/**
 * The design canvas: the post's composition, with the author's caret inside it.
 *
 * This is the whole point of the feature, so it is worth being exact about what is
 * on the screen. There is **one** visual surface — the design, drawn by
 * `PostDesignRenderer` — and the author types into *it*. There is no second, smaller
 * preview and no field beside the design: the words being typed are the words of the
 * design, in the design's own type, in the design's own slot.
 *
 * The trick that makes that possible without writing a rich-text engine is one
 * invisible layer. The composer's existing input is kept exactly as it is —
 * mentions, facets, paste, emoji, the keyboard, the selection, the character count,
 * every behaviour the post composer already had — but on a canvas it is *drawn
 * invisibly* (`designInputStyle`) and given the very metrics the design is drawn
 * with, which the renderer hands over as a render prop. So it types in the same
 * place, at the same size, with the same breaks as the words the author can see, and
 * what it contributes is the caret, the selection and the keyboard.
 *
 * A consequence worth stating: the words on screen are always the design's, not the
 * input's. A highlight, a Mursala or a template's geometry therefore cannot "lag"
 * behind the text, because the layer that draws them is the layer that draws all of
 * it.
 *
 * The input is only mounted for the *active* post, as the composer already mounts
 * its ref (`textInputRef`) only for the active one: a thread of designs has one
 * caret, not one per card.
 */
import {useMemo} from 'react'
import {Text as RNText, View} from 'react-native'
import {type RichText} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'

import {PostDesignRenderer} from '#/components/Post/PostDesignRenderer'
import {atoms as a} from '#/alf'
import {resolveStyledPost} from '#/state/postStyle/typography'
import {type StyledPost} from '#/state/postStyle/types'
import {TextInput} from '#/view/com/composer/text-input/TextInput'
import {type TextInputRef} from '#/view/com/composer/text-input/TextInput.types'

export function DesignCanvas({
  style,
  richtext,
  setRichText,
  textInputRef,
  backgroundImageUri,
  selection,
  onSelectionChange,
  onPhotoPasted,
  onNewLink,
  onError,
  onPressPublish,
  onFocus,
  autoFocus,
}: {
  style: StyledPost
  richtext: RichText
  setRichText: (richtext: RichText) => void
  textInputRef: React.RefObject<TextInputRef | null> | null
  /** The author's own picture, while they are still on their own device. */
  backgroundImageUri?: string
  /**
   * Where the author is in the words they are editing.
   *
   * The canvas *draws* this rather than letting the input draw it: the input's own
   * selection is a block of its invisible line boxes, which a browser or a device will
   * happily paint the length of the composition (`DESIGN_SELECTION_WASH`).
   */
  selection?: {start: number; end: number} | null
  onSelectionChange: (selection: {start: number; end: number}) => void
  onPhotoPasted: (uri: string) => void
  onNewLink: (uri: string) => void
  onError: (message: string) => void
  onPressPublish: (richtext: RichText) => void
  onFocus: () => void
  autoFocus?: boolean
}) {
  const {t: l} = useLingui()
  /*
   * The design's own Mursala stretches, resolved against the words that are there
   * — the same value the renderer draws with, so the input under it measures the
   * same wrap (`designMursalas`).
   */
  const mursalas = useMemo(
    () => resolveStyledPost(style, richtext.text).mursalas,
    [style, richtext.text],
  )

  return (
    <PostDesignRenderer
      testID="designCanvas"
      style={style}
      text={richtext.text}
      selection={selection}
      backgroundImageUri={backgroundImageUri}>
      {metrics => (
        <View style={a.flex_1}>
          {/*
           * The design's own invitation, in the design's own type — drawn by React
           * Native's own `Text`, since the app's would re-type it in Inter.
           */}
          {richtext.text.length === 0 ? (
            <RNText
              testID="designCanvasPlaceholder"
              style={{
                fontFamily: metrics.fontFamily,
                fontSize: metrics.fontSize * 0.7,
                lineHeight: metrics.lineHeight,
                textAlign: metrics.textAlign,
                color: metrics.color,
                opacity: 0.35,
                position: 'absolute',
                width: '100%',
              }}>
              {l`Write your post`}
            </RNText>
          ) : null}
          <TextInput
            ref={textInputRef}
            design={metrics}
            designMursalas={mursalas}
            onSelectionChange={onSelectionChange}
            richtext={richtext}
            setRichText={setRichText}
            /*
             * No placeholder of the input's own: this canvas invites the author in
             * the design's own type, in the design's own place, and an input that
             * said it too would be the words twice over.
             */
            webForceMinHeight={false}
            hasRightPadding={false}
            isActive
            autoFocus={autoFocus}
            onPhotoPasted={onPhotoPasted}
            onNewLink={onNewLink}
            onError={onError}
            onPressPublish={onPressPublish}
            onFocus={onFocus}
            accessible
            accessibilityLabel={l`Write post`}
          />
        </View>
      )}
    </PostDesignRenderer>
  )
}
